import { MODERATION_ERROR_CODES } from '@aysar/contracts';

import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

/** What this surface should say about a refusal, and what to offer next. */
export interface ViolationErrorCopy {
  /** A `violations:error.*` key. */
  i18nKey: string;
  /**
   * A colleague decided between this moderator opening the case and submitting.
   *
   * The queue polls, so two moderators on one row is the **ordinary** case, not
   * a fault — the case was genuinely undecided when it was opened. So the answer
   * is **re-read**, never retry: the dialog offers a refresh, and the verdict on
   * screen is somebody else's to keep.
   */
  isConflict: boolean;
  /**
   * The case is not there any more (404).
   *
   * Distinct from a conflict, because there is nothing to go and read: the
   * dialog closes rather than leaving a moderator looking at a row that no
   * longer exists.
   */
  isGone: boolean;
}

/**
 * The refusals this surface can meet, and the key that answers each.
 *
 * Codes come from the contracts constants rather than string literals (U3): they
 * are the same objects the api's `DomainError` subclasses read, so a rename is a
 * compile error instead of a silent fallback to "something went wrong".
 *
 * `violationAlreadyAppealed` and `violationNotAppealable` are deliberately
 * absent — they belong to the member's own `POST /moderation/violations/:id/appeal`,
 * and no staff route can produce them, so mapping them would be copy nothing
 * could ever show.
 */
const KEY_BY_CODE: Readonly<Record<string, string>> = {
  [MODERATION_ERROR_CODES.violationNotInReviewableState]: 'error.notReviewable',
  [MODERATION_ERROR_CODES.violationNotFound]: 'error.notFound',
  [MODERATION_ERROR_CODES.violationTypeKeyTaken]: 'error.keyTaken',
  [MODERATION_ERROR_CODES.violationTypeNotFound]: 'error.typeNotFound',
  // The editor locks the switch so this is never SENT; mapped all the same, so a
  // stale tab or a hand-made request meets the rule in the operator's language.
  [MODERATION_ERROR_CODES.violationTypeReviewLocked]: 'error.reviewLocked',
};

/** Turns an api refusal into what this surface says about it, or `null`. */
export function violationErrorCopy(error: ApiError | null | undefined): ViolationErrorCopy | null {
  if (error === null || error === undefined) {
    return null;
  }

  const i18nKey = KEY_BY_CODE[error.code];

  if (i18nKey === undefined) {
    return null;
  }

  return {
    i18nKey,
    /**
     * The STATUS decides, not the code alone.
     *
     * `moderation_violation_not_reviewable` arrives as a **409** from
     * `ViolationNotReviewableError` — a colleague got there first — and as a
     * **422** from `ViolationTypeRequiredError`, which means nothing raced and
     * the moderator has simply not named the rule yet. Offering "read it again"
     * for the second would send them away from the picker they need to use.
     */
    isConflict: error.status === 409,
    isGone: error.status === 404,
  };
}
